/** @jsxImportSource @emotion/react */
import { FC, useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Switch } from 'components/Switch'
import { toast } from 'utils/toast'
import { UPDATE_CLASSROOM_TAB } from 'queries/subject'
import { updateClassRoomTab, updateClassRoomTabVariables } from '__generatedTypes__/updateClassRoomTab'
import { ClassroomTabEnum } from '__generatedTypes__/globalTypes'
import { SubjectExtendedFull } from 'hooks/usePlatformsAndSubjects'
import * as classes from './styles'

export type ClassroomTabLiveSwitcherProps = {
  selectedSubject: Partial<SubjectExtendedFull> | null
  tabEnum: ClassroomTabEnum
}

export const ClassroomTabLiveSwitcher: FC<ClassroomTabLiveSwitcherProps> = ({ selectedSubject, tabEnum }) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateClassRoomTabMutation] = useMutation<updateClassRoomTab, updateClassRoomTabVariables>(
    UPDATE_CLASSROOM_TAB
  )

  const classroomTab = selectedSubject?.classroomTab?.find((tab) => tab.tabEnum === tabEnum)

  const isLive = classroomTab ? !classroomTab.isHidden : false

  const handleToggleLive = useCallback(
    async (checked: boolean) => {
      if (!classroomTab || isUpdating) return

      const newIsHidden = !checked

      setIsUpdating(true)

      try {
        await updateClassRoomTabMutation({
          variables: {
            params: {
              id: classroomTab.id,
              isHidden: newIsHidden,
            },
          },
          optimisticResponse: {
            updateClassRoomTab: {
              __typename: 'ClassroomTabType',
              id: classroomTab.id,
              tabName: classroomTab.tabName,
              tabEnum: classroomTab.tabEnum,
              order: classroomTab.order,
              isHidden: newIsHidden,
            },
          },
        })

        toast.success('Classroom tab updated successfully')
      } catch (e) {
        const error = e as Error
        if (error.name === 'ApolloError') {
          toast.error(error.message)
        } else {
          toast.error('Failed to update classroom tab')
        }
      } finally {
        setIsUpdating(false)
      }
    },
    [classroomTab, isUpdating, updateClassRoomTabMutation]
  )

  if (!classroomTab) {
    return null
  }

  return (
    <Switch
      label="Live"
      labelPlacement="start"
      checked={isLive}
      onChange={(_, checked) => handleToggleLive(checked)}
      disabled={!classroomTab || isUpdating}
      css={classes.switchLabel}
    />
  )
}
